import { useCallback, useEffect, useState } from 'react';
import {
  dailyopsApi,
  type BriefingRecord,
  type DailyOpsDashboard,
  type ForecastRecord,
  type HealthScoreRecord,
  type OpsRecommendation,
  type OpportunityRecord,
  type PriorityRecord,
  type RiskRecord,
} from '../../api/dailyops';

type Tab =
  | 'home'
  | 'brief'
  | 'timeline'
  | 'risks'
  | 'opportunities'
  | 'recommendations'
  | 'forecast';

function severityClass(s?: string) {
  if (s === 'critical') return 'text-red-700 bg-red-50';
  if (s === 'high') return 'text-amber-700 bg-amber-50';
  return 'text-slate-600 bg-slate-50';
}

function healthClass(score: number) {
  if (score >= 80) return 'text-emerald-700';
  if (score >= 65) return 'text-amber-700';
  return 'text-red-700';
}

function categoryLabel(c: string) {
  return c.replace(/_/g, ' ');
}

export function DailyOpsPage() {
  const [tab, setTab] = useState<Tab>('home');
  const [dashboard, setDashboard] = useState<DailyOpsDashboard | null>(null);
  const [briefings, setBriefings] = useState<BriefingRecord[]>([]);
  const [priorities, setPriorities] = useState<PriorityRecord[]>([]);
  const [forecasts, setForecasts] = useState<ForecastRecord[]>([]);
  const [health, setHealth] = useState<HealthScoreRecord | null>(null);
  const [recommendations, setRecommendations] = useState<OpsRecommendation[]>([]);
  const [risks, setRisks] = useState<RiskRecord[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, brief, pri, fc, hs, recs, rsk, opp] = await Promise.all([
        dailyopsApi.dashboard(),
        dailyopsApi.listBriefings(),
        dailyopsApi.listPriorities(),
        dailyopsApi.listForecasts(),
        dailyopsApi.listHealthScores(),
        dailyopsApi.listRecommendations(),
        dailyopsApi.listRisks(),
        dailyopsApi.listOpportunities(),
      ]);
      setDashboard(dash);
      setBriefings(brief);
      setPriorities(pri);
      setForecasts(fc);
      setHealth(hs[0] ?? null);
      setRecommendations(recs);
      setRisks(rsk);
      setOpportunities(opp);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load daily operations data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generateBriefing = async () => {
    setBusy(true);
    try {
      await dailyopsApi.generateBriefing({ type: 'morning' });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const generateRecs = async () => {
    setBusy(true);
    try {
      await dailyopsApi.generateRecommendations();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'home', label: 'Executive Home' },
    { id: 'brief', label: 'Daily Brief' },
    { id: 'timeline', label: 'Business Timeline' },
    { id: 'risks', label: 'Risk Console' },
    { id: 'opportunities', label: 'Opportunity Explorer' },
    { id: 'recommendations', label: 'Recommendation Workspace' },
    { id: 'forecast', label: 'Forecast Dashboard' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Daily Operations Center</h1>
          <p className="text-sm text-slate-500">
            Executive briefing, priorities, health scores &amp; AI operations copilot — FRP-00003
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              tab === t.id ? 'bg-sky-100 text-sky-700' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'home' && dashboard && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Executive Score', value: dashboard.overall_executive_score.toFixed(1) },
              { label: 'Critical Priorities', value: dashboard.critical_priorities.toString() },
              { label: 'Active Risks', value: dashboard.active_risks.toString() },
              { label: 'Opportunities', value: dashboard.active_opportunities.toString() },
              { label: 'Revenue Forecast', value: `$${dashboard.today_revenue_forecast.toLocaleString()}` },
              { label: 'Profit Forecast', value: `$${dashboard.today_profit_forecast.toLocaleString()}` },
              { label: 'Recommendations', value: dashboard.active_recommendations.toString() },
              { label: 'Modules Consolidated', value: dashboard.modules_consolidated.toString() },
            ].map((kpi) => (
              <div key={kpi.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">{kpi.value}</p>
              </div>
            ))}
          </div>

          {health && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="font-medium text-slate-900">Executive Health Score</h2>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                {[
                  { label: 'Business', score: health.business_health },
                  { label: 'Growth', score: health.growth_health },
                  { label: 'Profit', score: health.profit_health },
                  { label: 'Inventory', score: health.inventory_health },
                  { label: 'Advertising', score: health.advertising_health },
                  { label: 'Content', score: health.content_health },
                  { label: 'Brand', score: health.brand_health },
                  { label: 'Customer', score: health.customer_health },
                  { label: 'Marketplace', score: health.marketplace_health },
                  { label: 'Overall', score: health.overall_executive_score },
                ].map((h) => (
                  <div key={h.label} className="text-center">
                    <p className="text-xs text-slate-500">{h.label}</p>
                    <p className={`text-xl font-semibold ${healthClass(h.score)}`}>{h.score.toFixed(0)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-4 py-3">
              <h2 className="font-medium text-slate-900">Today&apos;s Priorities</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {priorities.slice(0, 5).map((p) => (
                <div key={p.id} className="flex flex-wrap items-start justify-between gap-2 px-4 py-3">
                  <div>
                    <p className="font-medium text-slate-900">{p.title}</p>
                    <p className="text-sm text-slate-500">{p.description}</p>
                  </div>
                  <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                    #{p.priority_rank}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'brief' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={generateBriefing}
              disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Generate Morning Brief
            </button>
          </div>
          {briefings.map((b) => (
            <div key={b.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-medium text-slate-900">{b.title}</h3>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{b.type}</span>
              </div>
              {b.summary && <p className="mt-2 text-sm text-slate-600">{b.summary}</p>}
              {b.highlights && b.highlights.length > 0 && (
                <ul className="mt-3 list-inside list-disc space-y-1 text-sm text-slate-700">
                  {b.highlights.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
              )}
              {b.key_metrics && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {b.key_metrics.map((m) => (
                    <span key={m} className="rounded bg-slate-50 px-2 py-1 text-xs text-slate-600">{m}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'timeline' && (
        <div className="space-y-3">
          {priorities.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-medium text-slate-900">{p.title}</h3>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                  {categoryLabel(p.category)}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-500">{p.expected_impact}</p>
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500">
                <span>Urgency: {(p.urgency_score * 100).toFixed(0)}%</span>
                <span>Confidence: {(p.confidence_score * 100).toFixed(0)}%</span>
                <span>Source: {p.source_module}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'risks' && (
        <div className="space-y-3">
          {risks.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-medium text-slate-900">{r.title}</h3>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${severityClass(r.severity)}`}>
                  {r.severity}
                </span>
              </div>
              {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
              {r.impact && <p className="mt-1 text-xs text-slate-500">Impact: {r.impact}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'opportunities' && (
        <div className="space-y-3">
          {opportunities.map((o) => (
            <div key={o.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-medium text-slate-900">{o.title}</h3>
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                  ROI {o.expected_roi.toFixed(1)}x
                </span>
              </div>
              {o.description && <p className="mt-1 text-sm text-slate-600">{o.description}</p>}
              <p className="mt-1 text-xs text-slate-500">
                Confidence: {(o.confidence_score * 100).toFixed(0)}% · {categoryLabel(o.category ?? '')}
              </p>
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={generateRecs}
              disabled={busy}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Generate AI Recommendations
            </button>
          </div>
          {recommendations.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="font-medium text-slate-900">{r.title}</h3>
                  {r.description && <p className="mt-1 text-sm text-slate-600">{r.description}</p>}
                </div>
                <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
                  Priority #{r.priority_rank}
                </span>
              </div>
              {r.evidence && (
                <p className="mt-2 rounded bg-slate-50 px-2 py-1 text-xs text-slate-600">
                  <span className="font-medium">Evidence:</span> {r.evidence}
                </p>
              )}
              {r.business_context && (
                <p className="mt-1 text-xs text-slate-500">Context: {r.business_context}</p>
              )}
              {r.next_steps && r.next_steps.length > 0 && (
                <ul className="mt-2 list-inside list-decimal text-xs text-slate-600">
                  {r.next_steps.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              )}
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                <span className="text-slate-500">Impact: {r.expected_impact}</span>
                <span className="text-slate-500">Confidence: {(r.confidence_score * 100).toFixed(0)}%</span>
                {r.requires_approval && (
                  <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">Requires approval</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'forecast' && forecasts.length > 0 && (
        <div className="space-y-4">
          {forecasts.map((f) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="font-medium text-slate-900">Today&apos;s Forecast</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  { label: 'Revenue', value: `$${f.revenue_forecast.toLocaleString()}` },
                  { label: 'Profit', value: `$${f.profit_forecast.toLocaleString()}` },
                  { label: 'Ad Spend', value: `$${f.advertising_spend.toLocaleString()}` },
                  { label: 'Cash Flow', value: `$${f.cash_flow_forecast.toLocaleString()}` },
                  { label: 'Inventory Consumption', value: `${f.inventory_consumption_units} units` },
                  { label: 'Operational Load', value: f.operational_load ?? '—' },
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-xs text-slate-500">{item.label}</p>
                    <p className="font-medium text-slate-900">{item.value}</p>
                  </div>
                ))}
              </div>
              {f.potential_risks && f.potential_risks.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-red-600">Potential Risks</p>
                  <ul className="mt-1 list-inside list-disc text-sm text-slate-600">
                    {f.potential_risks.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}
              {f.potential_opportunities && f.potential_opportunities.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-emerald-600">Potential Opportunities</p>
                  <ul className="mt-1 list-inside list-disc text-sm text-slate-600">
                    {f.potential_opportunities.map((o) => (
                      <li key={o}>{o}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
